/**
 * main.tsx — 앱 부팅 진입점 (ReactDOM.createRoot + RootProviders 트리).
 *
 * 【역할】
 * - React.StrictMode > ConfigProvider(antd locale) > App(antd) > QueryClientProvider > FrameworkProvider > RouterProvider.
 * - localStorage(fw.locale) 값으로 antd/dayjs locale 초기 설정 → LOCALE_CHANGE_EVENT 구독으로 런타임 전환.
 * - TanStack Query 전역 설정(refetchOnWindowFocus=false, retry=1).
 *
 * 【주요 기능】
 * - readInitialLocale() — SSR 안전 localStorage 접근 (try/catch, 기본 'ko').
 * - RootProviders — locale 상태 + LOCALE_CHANGE_EVENT 리스너로 ConfigProvider locale 실시간 동기화.
 * - StrictMode 이중 mount 영향을 받는 부작용 차단 (useEffect cleanup 정확성 강제).
 *
 * 【사용 위치】 index.html `<script type=module src=/src/main.tsx>` 로 부팅.
 *
 * 【포팅 원본】 Vite React 템플릿 + Spring PageMain.vue 초기화 파이프라인.
 *
 * 【의존성】
 * - react-router-dom RouterProvider + `@/router`
 * - `@/framework/FrameworkProvider` (message/codes/permissions/popup context)
 * - `@/framework/antLocale` (getAntdLocale) + `@/i18n` (i18next init side-effect)
 */
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { ConfigProvider, App as AntApp } from 'antd'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { router } from '@/router'
import { FrameworkProvider } from '@/framework/FrameworkProvider'
import { getAntdLocale } from '@/framework/antLocale'
import { LOCALE_CHANGE_EVENT } from '@/framework/useLocale'
import '@/i18n'
import 'antd/dist/reset.css'
import '@/styles/global.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 }
  }
})

function readInitialLocale(): string {
  try {
    return localStorage.getItem('fw.locale') || 'ko'
  } catch {
    return 'ko'
  }
}

function RootProviders() {
  const [locale, setLocale] = useState<string>(readInitialLocale)

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as string | undefined
      if (detail && detail !== locale) setLocale(detail)
    }
    window.addEventListener(LOCALE_CHANGE_EVENT, onChange as EventListener)
    return () => window.removeEventListener(LOCALE_CHANGE_EVENT, onChange as EventListener)
  }, [locale])

  return (
    <ConfigProvider locale={getAntdLocale(locale)}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <FrameworkProvider>
            <RouterProvider router={router} future={{ v7_startTransition: true }} />
          </FrameworkProvider>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RootProviders />
  </React.StrictMode>
)
